<template>
  <div>
    <div
      class="onboarding-options__options"
      :class="{
        'onboarding-options__options--wrap': page.wrapInputs,
      }"
    >
      <template v-for="option in options">
        <OptionCheckbox
          v-if="page.multipleOptions"
          :key="`a-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
          :maxOptions="page.maxOptions"
        />
        <OptionText
          v-else-if="option.type === 'text-input'"
          :key="`b-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
        />
        <OptionRadio
          v-else
          :key="`c-${option.label}`"
          v-model="selectedOptions"
          class="onboarding-options__option"
          :class="{ 'onboarding-options__option--center': page.centerText }"
          :option="option"
        />
      </template>
    </div>

    <button
      v-if="skipOption"
      class="onboarding-options__option-skip"
      type="button"
      @click="emit('skip')"
    >
      {{ skipOption.label }}
    </button>

    <img
      v-if="page.image && imageSrc"
      class="onboarding-options__image"
      :src="imageSrc"
      :width="page.image.width"
      :height="page.image.height"
      alt=""
    />
  </div>
</template>

<script setup lang="ts">
import type {
  QuestionnaireFormOption,
  QuestionnaireImage,
} from '@/models/onboarding.types'

import OptionRadio from '../OptionRadio.vue'
import OptionText from '../OptionText.vue'
import OptionCheckbox from '../OptionCheckbox.vue'

export type OptionsBodyPageFields = {
  multipleOptions?: boolean
  maxOptions?: number
  centerText?: boolean
  wrapInputs?: boolean
  image?: QuestionnaireImage
}

defineProps<{
  page: OptionsBodyPageFields
  options: QuestionnaireFormOption[]
  skipOption?: QuestionnaireFormOption
  imageSrc?: string
}>()

const selectedOptions = defineModel<string[]>({ required: true })

const emit = defineEmits<{
  skip: []
}>()
</script>

<style lang="scss" scoped>
.onboarding-options__options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  @media only screen and (min-width: $w-tablet-s) {
    margin-top: unset;
  }

  :deep(.option-input__label-wrapper:not(:has(img))::before) {
    content: '';
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    margin-right: 6px;
    background: url('@/assets.app/images/onboarding/option-sparkle.svg')
      no-repeat center / contain;
  }
}

.onboarding-options__options--wrap {
  flex-direction: row;
  flex-wrap: wrap;

  &:deep(.option-input__label) {
    width: 100%;
    text-align: center;
  }
}

.onboarding-options__option {
  flex: 1;
  min-width: fit-content;
}

.onboarding-options__option--center {
  :deep(.option-input__label-wrapper) {
    justify-content: center;
  }
}

.onboarding-options__option-skip {
  width: 100%;
  padding: 20px;
  margin-bottom: -20px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.02em;
  text-align: center;
  color: $c-black-3;
  @media only screen and (min-width: $w-tablet-s) {
    margin-top: auto;
  }

  @include hover {
    color: $c-black-15;
  }
}

.onboarding-options__image {
  width: auto;
  min-height: 0;
  margin-top: 16px;
  max-height: 100%;
}
</style>
